import { Delete, Edit } from '@mui/icons-material';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getBusinessNameByIdentifier } from 'lib/api/sii';
import { formatMoney } from 'lib/functions/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { DocumentImporterConfig, ImporterDocumentRow } from './types';
import { formatDateDisplay } from './utils';

export interface DocumentsTableProps {
  documents: ImporterDocumentRow[];
  config: DocumentImporterConfig;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

const useStakeholderNames = (
  documents: ImporterDocumentRow[],
  enabled: boolean,
): Record<string, string> => {
  const dispatch = useDispatch();
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const requestedRef = React.useRef<Set<string>>(new Set());

  const fetchName = useCallback(
    async (identifier: string) => {
      const id = identifier?.trim();
      if (!id) return;
      try {
        const res = await getBusinessNameByIdentifier(dispatch, {
          identifier: id,
        });
        if (res?.name) {
          setResolved((prev) => ({ ...prev, [id]: res.name }));
        }
      } catch {
        // ignore: keep current stakeholderName or empty
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!enabled) return;
    documents.forEach((row) => {
      const id = row.stakeholderIdentifier?.trim();
      if (!id || row.stakeholderName) return;
      if (requestedRef.current.has(id)) return;
      requestedRef.current.add(id);
      void fetchName(id);
    });
  }, [enabled, documents, fetchName]);

  return resolved;
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  config,
  onEdit,
  onDelete,
}) => {
  const hasType = config.manualFormFields.some((f) => f.key === 'type');
  const typeLabel =
    config.manualFormFields.find((f) => f.key === 'type')?.label ?? 'Tipo';
  const hasStakeholderName = config.manualFormFields.some(
    (f) => f.key === 'stakeholderName',
  );
  const stakeholderNameLabel =
    config.manualFormFields.find((f) => f.key === 'stakeholderName')?.label ??
    'Razón social';
  const showStakeholderNameColumn = Boolean(config.validateWithSII);
  const stakeholderNames = useStakeholderNames(
    documents,
    showStakeholderNameColumn,
  );

  if (documents.length === 0) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {documents.length} documento(s) agregado(s)
      </Typography>
      <TableContainer
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {hasType && <TableCell>{typeLabel}</TableCell>}
              <TableCell>Folio</TableCell>
              <TableCell>RUT emisor</TableCell>
              <TableCell>RUT deudor</TableCell>
              {showStakeholderNameColumn && (
                <TableCell>Razón social deudor</TableCell>
              )}
              {hasStakeholderName && (
                <TableCell>{stakeholderNameLabel}</TableCell>
              )}
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell align="right" width={100}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((row, index) => (
              <TableRow key={row.id ?? `${row.folio}-${index}`}>
                {hasType && (
                  <TableCell>
                    {config.documentTypeOptions?.find(
                      (o) => o.value === row.type,
                    )?.label ??
                      row.type ??
                      '—'}
                  </TableCell>
                )}
                <TableCell>{row.folio}</TableCell>
                <TableCell>{row.businessIdentifier}</TableCell>
                <TableCell>{row.stakeholderIdentifier}</TableCell>
                {showStakeholderNameColumn && (
                  <TableCell>
                    {row.stakeholderName ??
                      stakeholderNames[
                        row.stakeholderIdentifier?.trim() ?? ''
                      ] ??
                      '—'}
                  </TableCell>
                )}
                {hasStakeholderName && (
                  <TableCell>{row.stakeholderName ?? '—'}</TableCell>
                )}
                <TableCell>{formatDateDisplay(row.issuedDate)}</TableCell>
                <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Editar"
                    onClick={() => onEdit(index)}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Eliminar"
                    onClick={() => onDelete(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default DocumentsTable;
